import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import {
  input,
  InputState,
  inputGroup,
  InputGroupState,
} from '../../components/input';
import { notifyError, notifySuccess } from '../../services/notifications';
import { ApiUploadRequestParams } from '../../api';
import { DEFAULT_SPECIALITY_ANCHOR, Routes } from '../../routes/routes';

type WizardStep = 'context' | 'upload' | 'preview';

type Props = {
  teamId: string;
};

// Raw GraphQL operations for custom specialty mutations.
// These are used via runRequest() with raw strings because the operations
// are not yet included in the generated SDK types from @clipr/lib.
const CREATE_CUSTOM_SPECIALTY_MUTATION = `
  mutation CreateCustomSpecialty($args: CreateCustomSpecialtyInput!) {
    createCustomSpecialty(args: $args) {
      customSpecialty {
        id
        teamId
        name
        status
        version
      }
      uploadUrl
      uploadFields {
        key
        value
      }
    }
  }
`;

const REPLACE_SAMPLE_UPLOAD_MUTATION = `
  mutation RequestSampleReplacementUpload($teamId: ID!, $specialtyId: ID!, $fileName: String!) {
    requestCustomSpecialtySampleReplacementUpload(teamId: $teamId, specialtyId: $specialtyId, fileName: $fileName) {
      s3Key
      uploadUrl
      uploadFields {
        key
        value
      }
    }
  }
`;

const GENERATE_CUSTOM_SPECIALTY_PROMPT_MUTATION = `
  mutation GenerateCustomSpecialtyPrompt(
    $teamId: ID!, $specialtyId: ID!, $name: String, $description: String, $sampleReportS3Key: String
  ) {
    generateCustomSpecialtyPrompt(
      teamId: $teamId, specialtyId: $specialtyId, name: $name, description: $description,
      sampleReportS3Key: $sampleReportS3Key
    ) {
      customSpecialty {
        id
        name
        status
        generatedPrompt
        promptVersion
        version
      }
      extractedStructure
    }
  }
`;

const CUSTOM_SPECIALTY_TRAINING_QUERY = `
  query GetCustomSpecialtyTraining($teamId: ID!, $specialtyId: ID!) {
    getCustomSpecialty(teamId: $teamId, specialtyId: $specialtyId) {
      id
      generatedPrompt
      promptVersion
      parsedSampleStructure
      trainingError
    }
  }
`;

const EDITABLE_CUSTOM_SPECIALTIES_QUERY = `
  query GetEditableCustomSpecialties($teamId: ID!) {
    getCustomSpecialties(teamId: $teamId) { id name description status clonedFromSpecialtyId }
  }
`;

const RENAME_CUSTOM_SPECIALTY_MUTATION = `
  mutation RenameCustomSpecialty($args: UpdateCustomSpecialtyInput!) {
    updateCustomSpecialty(args: $args) { id name }
  }
`;

const ACTIVATE_CUSTOM_SPECIALTY_MUTATION = `
  mutation ActivateCustomSpecialty($teamId: ID!, $specialtyId: ID!) {
    activateCustomSpecialty(teamId: $teamId, specialtyId: $specialtyId) {
      id
      name
      status
      version
    }
  }
`;

// Response types for the raw GraphQL operations above.
// These will become unnecessary once the operations are added to the generated SDK.
type CreateCustomSpecialtyResponse = {
  createCustomSpecialty: {
    customSpecialty: { id: string; teamId: string; name: string; status: string; version: number };
    uploadUrl: string;
    uploadFields: Array<{ key: string; value: string }>;
  };
};

type GeneratePromptResponse = {
  generateCustomSpecialtyPrompt: {
    customSpecialty: {
      id: string;
      name: string;
      status: string;
      generatedPrompt: string | null;
      promptVersion: number | null;
      version: number;
    };
    extractedStructure: string | null;
  };
};

type TrainingStatusResponse = {
  getCustomSpecialty: {
    id: string;
    generatedPrompt: string | null;
    promptVersion: number | null;
    parsedSampleStructure: string | null;
    trainingError: string | null;
  } | null;
};

type EditableTemplate = { id: string; name: string; description: string; status: string };

type EditableTemplatesResponse = {
  getCustomSpecialties: Array<EditableTemplate & { clonedFromSpecialtyId: string | null }>;
};

const TRAINING_POLL_INTERVAL_MS = 3000;
// The worker Lambda gives up at 5 minutes; a little past that, nothing more is coming.
const TRAINING_POLL_TIMEOUT_MS = 6 * 60 * 1000;

export class TrainPageState extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'reset'>(this, {
      currentStep: observable,
      teamId: observable,
      isLoading: observable,
      selectedFile: observable,
      uploadProgress: observable,
      isUploading: observable,
      fileUploaded: observable,
      specialtyId: observable,
      generatedPromptPreview: observable,
      extractedStructure: observable,
      isGeneratingPrompt: observable,
      isEditing: observable,
      editableTemplates: observable,
      editingStatus: observable,
      canAdvanceToUpload: computed,
      isNameOnlyEdit: computed,
      canUpload: computed,
      canActivate: computed,
      pageMounted: action,
      pageUnmounted: action,
      reset: action,
      goToStep: action,
      handleContextSubmit: action,
      setSelectedFile: action,
      handleFileUpload: action,
      handleGeneratePrompt: action,
      handleActivate: action,
      handleSaveAsDraft: action,
      setEditing: action,
      selectTemplate: action,
    });

    this.specialtyName = input(this, {
      name: 'specialtyName',
      isRequired: true,
      placeholder: 'Report Format Name',
    });

    this.description = input(this, {
      name: 'description',
      isRequired: true,
      multiline: true,
      placeholder: 'Describe the report format and any specific instructions...',
    });

    this.contextForm = inputGroup(this, {
      name: 'contextForm',
      inputs: () => [
        this.specialtyName,
        this.description,
      ],
    });
  }

  readonly specialtyName: InputState;
  readonly description: InputState;
  readonly contextForm: InputGroupState;

  currentStep: WizardStep = 'context';
  teamId: string = '';
  isLoading: boolean = false;
  selectedFile: File | null = null;
  uploadProgress: number = 0;
  isUploading: boolean = false;
  fileUploaded: boolean = false;

  specialtyId: string | null = null;
  generatedPromptPreview: string | null = null;
  extractedStructure: string | null = null;
  isGeneratingPrompt: boolean = false;
  isEditing: boolean = false;
  editableTemplates: EditableTemplate[] = [];
  editingStatus: string | null = null;

  get canAdvanceToUpload(): boolean {
    return (!this.isEditing || !!this.specialtyId) &&
      !this.contextForm.error &&
      !!this.specialtyName.value &&
      !!this.description.value;
  }

  // The name alone doesn't change what a report says, so it is saved without retraining.
  get isNameOnlyEdit(): boolean {
    const template = this.editableTemplates.find(t => t.id === this.specialtyId);
    return this.isEditing && !this.selectedFile && template?.description === this.description.value;
  }

  get canUpload(): boolean {
    return !!this.selectedFile && !this.isUploading;
  }

  get canActivate(): boolean {
    return !!this.specialtyId && !!this.generatedPromptPreview;
  }

  async pageMounted({ teamId }: Props) {
    this.teamId = teamId || '';
    this.reset();

    const pageTeamId = this.teamId;
    if (this.store.user && pageTeamId && !this.store.teamManager.hasTeam(pageTeamId)) {
      this.isLoading = true;

      const [, err] = await this.store.teamManager.apiFetchTeam({ id: pageTeamId });

      runInAction(() => {
        if (this.teamId === pageTeamId)
          this.isLoading = false;
      });

      if (err || this.teamId !== pageTeamId)
        return;
    }

    this.startAdminGuidedTraining();
  }

  pageUnmounted() {
    this.store.adminGuidedTraining.handleCreatePageUnmounted(this.teamId);
    this.reset();
  }

  private reset() {
    this.currentStep = 'context';
    this.isLoading = false;
    this.selectedFile = null;
    this.uploadProgress = 0;
    this.isUploading = false;
    this.fileUploaded = false;
    this.specialtyId = null;
    this.generatedPromptPreview = null;
    this.extractedStructure = null;
    this.isGeneratingPrompt = false;
    this.isEditing = false;
    this.editableTemplates = [];
    this.editingStatus = null;
    this.contextForm.clear();
  }

  async setEditing(isEditing: boolean) {
    this.isEditing = isEditing;
    this.specialtyId = null;
    this.editingStatus = null;
    this.contextForm.clear();
    if (!isEditing || this.editableTemplates.length) return;

    const [res, err] = await this.store.apiService.runRequest<EditableTemplatesResponse>(
      EDITABLE_CUSTOM_SPECIALTIES_QUERY,
      { teamId: this.teamId },
    );
    if (err || !res) {
      notifyError(this, 'Could not load the templates in this library.');
      return;
    }
    runInAction(() => {
      // Clones are retrained through their agency's source format, so they can't be edited here.
      this.editableTemplates = res.getCustomSpecialties
        .filter(t => t.status !== 'Archived' && !t.clonedFromSpecialtyId)
        .map(({ id, name, description, status }) => ({ id, name, description, status }));
    });
  }

  selectTemplate(id: string) {
    const template = this.editableTemplates.find(t => t.id === id);
    this.specialtyId = template?.id ?? null;
    this.editingStatus = template?.status ?? null;
    this.specialtyName.loadValue(template?.name ?? '');
    this.description.loadValue(template?.description ?? '');
  }

  private startAdminGuidedTraining() {
    const resumed = this.store.adminGuidedTraining.handleCreatePageMounted(this.teamId);
    if (resumed || !this.teamId) return;

    const team = this.store.teamManager.getTeam(this.teamId);
    this.store.adminGuidedTraining.openCreate(
      { teamId: this.teamId },
      !!team?.hasPermission('EditTeam'),
      team,
    );
  }

  goToStep(step: WizardStep) {
    this.currentStep = step;
  }

  handleContextSubmit() {
    this.contextForm.handleSubmit();

    if (this.contextForm.error) {
      notifyError(this, 'Please fill in all required fields.');
      return;
    }

    this.currentStep = 'upload';
    this.store.adminGuidedTraining.handleCreateContextSubmitted();
  }

  async saveName() {
    const [, err] = await this.store.apiService.runRequest(
      RENAME_CUSTOM_SPECIALTY_MUTATION,
      { args: { teamId: this.teamId, specialtyId: this.specialtyId, name: this.specialtyName.value } },
    );
    if (err) {
      notifyError(this, 'Could not save the template name. Please try again.');
      return;
    }
    notifySuccess(this, 'Template saved.');
    this.store.goTo(Routes.teamSettings(this.teamId));
  }

  setSelectedFile(file: File | null) {
    this.selectedFile = file;
  }

  async handleFileUpload() {
    if (!this.selectedFile || !this.teamId) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw GraphQL operation not yet in generated SDK
    const [createRes, createErr] = await this.store.apiService.runRequest<any>(
      CREATE_CUSTOM_SPECIALTY_MUTATION,
      {
        args: {
          teamId: this.teamId,
          name: this.specialtyName.value,
          description: this.description.value,
          sampleReportFileName: this.selectedFile.name,
        },
      }
    );

    if (createErr || !createRes) {
      runInAction(() => {
        this.isUploading = false;
        notifyError(this, 'Could not create the custom report format. Please try again.');
      });
      return;
    }

    const response = createRes as CreateCustomSpecialtyResponse;
    const { customSpecialty, uploadUrl, uploadFields } = response.createCustomSpecialty;

    runInAction(() => {
      this.specialtyId = customSpecialty.id;
    });

    const uploadParams: ApiUploadRequestParams = {
      file: this.selectedFile!,
      url: uploadUrl,
      data: uploadFields,
    };

    const uploadRequest = this.store.apiService.uploadRequest(uploadParams);
    const [, uploadErr] = await uploadRequest.start();

    if (uploadErr) {
      runInAction(() => {
        this.isUploading = false;
        notifyError(this, 'Could not upload the sample file. Please try again.');
      });
      return;
    }

    runInAction(() => {
      this.isUploading = false;
      this.fileUploaded = true;
      this.uploadProgress = 1;
      notifySuccess(this, 'Sample file uploaded successfully.');
    });
  }

  async handleGeneratePrompt() {
    if (!this.specialtyId || !this.teamId) return;

    this.isGeneratingPrompt = true;

    // An edit with a new sample uploads it first; the template only switches to it if training succeeds.
    const sampleReportS3Key = this.isEditing && this.selectedFile ? await this.uploadReplacementSample() : undefined;
    if (sampleReportS3Key === null) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw GraphQL operation not yet in generated SDK
    const [res, err] = await this.store.apiService.runRequest<any>(
      GENERATE_CUSTOM_SPECIALTY_PROMPT_MUTATION,
      {
        teamId: this.teamId,
        specialtyId: this.specialtyId,
        ...(this.isEditing && { name: this.specialtyName.value, description: this.description.value }),
        ...(sampleReportS3Key && { sampleReportS3Key }),
      }
    );

    if (err || !res) {
      runInAction(() => {
        this.isGeneratingPrompt = false;
        notifyError(this, `Could not generate the report preview. ${err instanceof Error ? err.message : 'Please try again.'}`);
      });
      return;
    }

    // The mutation only queues the run -- it takes minutes, far past what AppSync will hold a
    // request open for -- so the result is collected from the record instead.
    const response = res as GeneratePromptResponse;
    const { customSpecialty } = response.generateCustomSpecialtyPrompt;

    await this.awaitTrainingResult(this.specialtyId, customSpecialty.promptVersion ?? 0);
  }

  /** Returns the uploaded key, or null after reporting why the upload failed. */
  private async uploadReplacementSample(): Promise<string | null> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw GraphQL operation not yet in generated SDK
    const [res, err] = await this.store.apiService.runRequest<any>(
      REPLACE_SAMPLE_UPLOAD_MUTATION,
      { teamId: this.teamId, specialtyId: this.specialtyId, fileName: this.selectedFile!.name },
    );
    const slot = (res as { requestCustomSpecialtySampleReplacementUpload?: {
      s3Key: string; uploadUrl: string; uploadFields: Array<{ key: string; value: string }>;
    } } | undefined)?.requestCustomSpecialtySampleReplacementUpload;
    if (!err && slot) {
      const params = { file: this.selectedFile!, url: slot.uploadUrl, data: slot.uploadFields };
      const [, uploadErr] = await this.store.apiService.uploadRequest(params).start();
      if (!uploadErr) return slot.s3Key;
    }

    runInAction(() => {
      this.isGeneratingPrompt = false;
      notifyError(this, 'Could not upload the new sample file. Please try again.');
    });
    return null;
  }

  private async awaitTrainingResult(specialtyId: string, previousPromptVersion: number) {
    const deadline = Date.now() + TRAINING_POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await new Promise(resolve => { setTimeout(resolve, TRAINING_POLL_INTERVAL_MS); });

      // The wizard was left, or restarted against a different format: this run is nobody's result.
      if (this.specialtyId !== specialtyId || !this.isGeneratingPrompt) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw GraphQL operation not yet in generated SDK
      const [res, err] = await this.store.apiService.runRequest<any>(
        CUSTOM_SPECIALTY_TRAINING_QUERY,
        { teamId: this.teamId, specialtyId }
      );

      // A failed poll says nothing about the run, which is still going; keep asking until the
      // deadline rather than reporting a failure the worker never had.
      if (err || !res) continue;

      const specialty = (res as TrainingStatusResponse).getCustomSpecialty;
      if (!specialty) continue;

      if (specialty.trainingError) {
        runInAction(() => {
          this.isGeneratingPrompt = false;
          notifyError(this, `Could not generate the report preview. ${specialty.trainingError}`);
        });
        return;
      }

      if ((specialty.promptVersion ?? 0) > previousPromptVersion) {
        runInAction(() => {
          this.isGeneratingPrompt = false;
          this.generatedPromptPreview = specialty.generatedPrompt || null;
          this.extractedStructure = specialty.parsedSampleStructure || null;
          this.currentStep = 'preview';
          this.store.adminGuidedTraining.handleCreatePreviewReady();
        });
        return;
      }
    }

    runInAction(() => {
      this.isGeneratingPrompt = false;
      notifyError(this, 'The report preview is taking longer than expected. Please try again.');
    });
  }

  async handleActivate() {
    if (!this.specialtyId || !this.teamId) return;

    this.isLoading = true;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw GraphQL operation not yet in generated SDK
    const [, err] = await this.store.apiService.runRequest<any>(
      ACTIVATE_CUSTOM_SPECIALTY_MUTATION,
      {
        teamId: this.teamId,
        specialtyId: this.specialtyId,
      }
    );

    if (err) {
      runInAction(() => {
        this.isLoading = false;
        notifyError(this, 'Could not activate the report format. Please try again.');
      });
      return;
    }

    await this.store.teamManager.apiFetchTeam({ id: this.teamId });

    runInAction(() => {
      this.isLoading = false;
      notifySuccess(this, 'Custom report format activated successfully.');
      this.store.adminGuidedTraining.handleCreateActivated();
      this.store.goTo({ pathname: Routes.teamSettings(this.teamId), hash: DEFAULT_SPECIALITY_ANCHOR });
    });
  }

  handleSaveAsDraft() {
    if (!this.specialtyId) {
      notifyError(this, 'No custom report format created yet. Complete the upload step first.');
      return;
    }
    notifySuccess(this, 'Report format saved as draft.');
    // A draft can't be the Library default, so Settings has nothing for it; go back where Train started.
    this.store.goTo(Routes.teamDashboard(this.teamId));
  }
}
